import { chatTime } from '@/components/sidebar/tabs/chat-tabs.util';
import { getApiBase } from '@/lib/api-base';
import { fetchWebtokenHeadless } from '@/store/auth-helper';
import { useSyncExternalStore } from 'react';
import { toast } from 'sonner';

/**
 * The titles users give their chats, Clu's and the agents' alike (one route,
 * `PATCH /agent/conversations/{id}`, renames both). The server keeps the
 * title and when the user gave it (`renamed_at`, which every list and read
 * carries), so a chat the user named is theirs in every tab and browser: its
 * title wins over the agent's name on its tab and over the first-message
 * placeholder on the Chats rail. A rename shows at once wherever a chat is
 * listed: every reader of the lists lays the titles given in this tab over
 * its own (withChatTitles) until it reads that rename, or a later one,
 * itself.
 */

/** The server's cap on a chat's title. */
export const CHAT_TITLE_MAX = 255;

/** A chat the user named, as the lists carry it. */
export interface RenamedMark {
  /** The user named it: the server says so, or a rename here is shown. */
  renamed?: boolean;
  /** When the user last named it, the server's own field; null if never. */
  renamed_at?: string | null;
}

/** Whether the user named the chat, here or anywhere else. */
export const isRenamed = (chat: RenamedMark): boolean =>
  chat.renamed === true || Boolean(chat.renamed_at);

/** A title given in this tab. */
export interface GivenTitle {
  title: string;
  /** The server's `renamed_at` for it; null until the server has it. */
  savedAt: string | null;
}

export interface ChatTitles {
  /** Titles given in this tab, by chat: shown over the lists' own. */
  given: ReadonlyMap<string, GivenTitle>;
}

let current: ChatTitles = { given: new Map() };
const listeners = new Set<() => void>();

function publish(given: ReadonlyMap<string, GivenTitle>): void {
  current = { given };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const read = () => current;

/** The titles given in this tab, re-rendering as they change. */
export function useChatTitles(): ChatTitles {
  return useSyncExternalStore(subscribe, read, read);
}

/** For tests: forgets every title given. */
export function resetChatTitles(): void {
  publish(new Map());
}

/**
 * Whether a title given here still shows over a listed chat: until the list
 * holds that rename or a later one, from this tab or anywhere else. While
 * the rename is out, and over a list read before it, it shows.
 */
function laysOver(given: GivenTitle, chat: RenamedMark): boolean {
  if (given.savedAt === null || !chat.renamed_at) return true;
  return chatTime(chat.renamed_at) < chatTime(given.savedAt);
}

/**
 * The chats with the titles given here laid over their own, each chat the
 * user named marked `renamed`. The same array when nothing changes.
 */
export function withChatTitles<
  T extends { id: string; title: string | null } & RenamedMark,
>(chats: readonly T[], { given }: ChatTitles): T[] {
  let changed = false;
  const next = chats.map((chat) => {
    const title = given.get(chat.id);
    if (title && laysOver(title, chat)) {
      changed = true;
      return { ...chat, title: title.title, renamed: true };
    }
    if (chat.renamed_at && !chat.renamed) {
      changed = true;
      return { ...chat, renamed: true };
    }
    return chat;
  });
  return changed ? next : (chats as T[]);
}

/** A title as the server keeps it: whitespace collapsed, at most the cap. */
export function cleanChatTitle(title: string): string {
  return title.split(/\s+/).filter(Boolean).join(' ').slice(0, CHAT_TITLE_MAX);
}

/**
 * Renames a chat: the title shows at once, the server keeps it, and a
 * failure puts the title back and says so. True when the server has it.
 */
export async function renameChat(id: string, title: string): Promise<boolean> {
  const next = cleanChatTitle(title);
  if (!next) return false;
  const previous = current.given.get(id);
  const mine: GivenTitle = { title: next, savedAt: null };
  publish(new Map(current.given).set(id, mine));
  try {
    const token = await fetchWebtokenHeadless();
    const response = await fetch(
      `${getApiBase()}/api/v1/agent/conversations/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title: next }),
      }
    );
    if (!response.ok) throw new Error(`Rename failed: ${response.status}`);
    const saved = await response.json().then(
      (body: { title?: unknown; renamed_at?: unknown } | null) => body,
      () => null
    );
    // Another rename of the chat since has the last word.
    if (current.given.get(id) !== mine) return true;
    publish(
      new Map(current.given).set(id, {
        // The server's own copy, where it cleaned the title differently.
        title:
          typeof saved?.title === 'string' && saved.title ? saved.title : next,
        savedAt:
          typeof saved?.renamed_at === 'string' ? saved.renamed_at : null,
      })
    );
    return true;
  } catch {
    if (current.given.get(id) !== mine) return false;
    const given = new Map(current.given);
    if (previous === undefined) given.delete(id);
    else given.set(id, previous);
    publish(given);
    toast.error("Couldn't rename the chat");
    return false;
  }
}
